// src/components/padrekko/qc/ReclamationQcTab.jsx
//
// Top-level Reclamation QC tab body. Three-state view machine:
//   - No session loaded             → QcPicker
//   - Session loaded, run complete  → QcSummary  (operator can opt into
//     viewing pads via forceInspector to drop back to the inspector for
//     review — Back to summary link in the inspector header returns)
//   - Session loaded, run active OR
//     completed-but-operator-said-view-pads → QcInspector
//
// "Run complete" = every pad in pads array has status in
// (completed | skipped). Derived from session.pads — when the last pending
// /in_progress pad's status flips via skipPad/finishPad, isComplete trips
// true and the tab re-renders with QcSummary automatically. No "click to
// see results" intermediate step; the Finish click is the confirmation.
//
// forceInspector is local UI state — defaults false so the natural flow
// goes Inspector → (last pad finishes) → Summary. Reset whenever the
// session changes (different session, or session cleared).

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2, AlertTriangle } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import QcPicker from "./QcPicker";
import QcInspector from "./QcInspector";
import QcSummary from "./QcSummary";

function deriveIsComplete(session) {
  const pads = session?.pads ?? [];
  if (pads.length === 0) return false;
  return pads.every((p) => p.status === "completed" || p.status === "skipped");
}

export default function ReclamationQcTab() {
  const session = useQcStore((s) => s.session);
  const caseInfo = useQcStore((s) => s.caseInfo);
  const loadCase = useQcStore((s) => s.loadCase);
  const loading = useQcStore((s) => s.loading);
  const error = useQcStore((s) => s.error);

  // Phase 4 — case deep-link. /map/padrekko?caseId=<id> auto-loads the case so
  // an emailed link lands the user directly in the inspector/summary instead
  // of the picker. strict:false reads the param without coupling this deep
  // component to the route definition.
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const deepLinkCaseId =
    typeof search?.caseId === "string" && search.caseId ? search.caseId : null;

  // De-dup the auto-load: a ref (effect-only, never read in render) so a
  // 403/404 doesn't retry-loop and a success doesn't re-fire. A genuinely new
  // caseId differs from the ref and re-fires. Render decisions below come from
  // the store's loading/error slots, not this ref.
  const attemptedRef = useRef(null);
  useEffect(() => {
    if (!deepLinkCaseId) return;
    if (caseInfo?.id === deepLinkCaseId) return;       // already loaded
    if (attemptedRef.current === deepLinkCaseId) return; // already tried
    attemptedRef.current = deepLinkCaseId;
    loadCase(deepLinkCaseId, { preserveCursor: false });
  }, [deepLinkCaseId, caseInfo?.id, loadCase]);

  // Once the case is loaded, strip ?caseId from the URL (one-shot entry). This
  // prevents a dangling param from stranding the user behind the loading gate
  // after they exit back to the picker (clearSession nulls caseInfo but can't
  // touch the URL). On error the param is intentionally kept so the error panel
  // persists; "Browse all cases" clears it explicitly.
  useEffect(() => {
    if (deepLinkCaseId && caseInfo?.id === deepLinkCaseId) {
      navigate({
        to: "/map/$tab",
        params: { tab: "padrekko" },
        search: (prev) => ({ ...prev, caseId: undefined }),
        replace: true,
      });
    }
  }, [deepLinkCaseId, caseInfo?.id, navigate]);

  // "Operator chose to view pads even though the run is complete." Reset
  // on session change so a fresh resume lands on Summary, not Inspector.
  // React-recommended pattern: track prev session id in state, compare
  // during render. Avoids the lint anti-pattern of setState-in-useEffect
  // for derived state — and is one fewer render pass than the effect
  // version. See "Adjusting some state when a prop changes" in the
  // React docs.
  //
  // NB: declared before any conditional return below (rules-of-hooks).
  const [forceInspector, setForceInspector] = useState(false);
  const [prevSessionId, setPrevSessionId] = useState(session?.id ?? null);
  if (prevSessionId !== (session?.id ?? null)) {
    setPrevSessionId(session?.id ?? null);
    setForceInspector(false);
  }

  // Deep-link pending / error gates — only while the target case isn't loaded.
  // Once loadCase succeeds (caseInfo.id === deepLinkCaseId, session set) this
  // falls through to the normal view machine. Driven purely by store state:
  // loadCase clears `error` and sets `loading` on entry, so a failed load
  // leaves error set + loading false → error panel; otherwise → pending.
  const deepLinkUnloaded = deepLinkCaseId && caseInfo?.id !== deepLinkCaseId;
  if (deepLinkUnloaded) {
    if (error && !loading) {
      return (
        <DeepLinkError
          message={error}
          onBrowse={() =>
            navigate({
              to: "/map/$tab",
              params: { tab: "padrekko" },
              search: (prev) => ({ ...prev, caseId: undefined }),
            })
          }
        />
      );
    }
    return <DeepLinkPending />;
  }

  if (!session) return <QcPicker />;

  const isComplete = deriveIsComplete(session);

  if (isComplete && !forceInspector) {
    return <QcSummary onViewPads={() => setForceInspector(true)} />;
  }

  return (
    <QcInspector
      onBackToSummary={isComplete ? () => setForceInspector(false) : null}
    />
  );
}

// Phase 4 — deep-link loading state while the case is fetched.
function DeepLinkPending() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-400">
      <Loader2 size={18} className="animate-spin" />
      <div className="text-[11px] font-mono uppercase tracking-widest">Opening case…</div>
    </div>
  );
}

// Phase 4 — deep-link failure (no access / not found / load error). The server
// returns 403 for non-participants and 404 for a missing case; both surface
// here via the store's error slot.
function DeepLinkError({ message, onBrowse }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 px-6 text-center">
      <AlertTriangle size={22} className="text-amber-400" />
      <div className="text-sm font-medium text-slate-200">Can’t open this case</div>
      <div className="text-[11px] text-slate-400 leading-relaxed max-w-xs">
        It may have been closed, removed, or you may not have access to it.
        {message ? <span className="block mt-1 text-fg-secondary">{message}</span> : null}
      </div>
      <button
        type="button"
        onClick={onBrowse}
        className="mt-1 text-[11px] px-3 py-1.5 rounded border
                   border-slate-700 text-slate-200
                   hover:bg-slate-800 hover:border-slate-600 transition"
      >
        Browse all cases
      </button>
    </div>
  );
}
